/**
 * AIExplainerPanel.jsx — Tier-S #1 from CANVAS_V3_DIFFERENTIATORS.md.
 *
 * When a node is selected on /canvas-v3, this panel auto-fetches an
 * AI-generated, two-tier explanation of what the node does:
 *
 *   • BUSINESS view (default, for domain/business stakeholders)
 *       - One-sentence "what is this?"
 *       - "Why is it here?" — the workflow context
 *       - "What if it fails?" — the impact / risk story
 *       - "Who depends on it?" — the systems / teams downstream
 *
 *   • TECHNICAL view (collapsed by default, "Show technical details" toggle)
 *       - Config breakdown (URL / method / headers / body / retry)
 *       - jq / expression notes
 *       - Edge cases (status codes, timeouts, retry semantics)
 *       - Performance hints (avg duration when execution data exists)
 *
 * Architecture
 * ────────────
 * - Standalone React component: takes `node`, `connections`,
 *   `executionState`, `installedConnectors`. No host state.
 * - Reuses the existing `aiCanvasDebug` API endpoint with a focused
 *   sub-graph (selected node + 1-hop neighbors) and a prompt that
 *   instructs the model to return a `## BUSINESS` and `## TECHNICAL`
 *   block. Parses the response client-side; falls back to a single
 *   plain-text body if either marker is missing.
 * - Caches results in component state keyed by node id so re-selecting
 *   the same node doesn't re-call the API.
 * - Manual "Re-explain" button busts the cache for the current node.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Sparkles, ChevronDown, ChevronUp, RefreshCw, Loader, AlertTriangle } from 'lucide-react';
import { aiCanvasDebug } from '../../services/api';
import { makeKey as makeCacheKey, readEntry, writeEntry, evictEntry } from '../../utils/aiExplainerCache';

// ── Type-aware prompts ────────────────────────────────────────────
// Each container/control-flow type gets a specialised framing so the
// Business view actually says something rich rather than the same
// generic "what is this step" template that a plain http_task gets.
// The Technical block is also tuned per type — a Switch's edge cases
// are completely different from an HTTP task's, and the user shouldn't
// have to mentally translate.
//
// All prompts emit the same two headings (## BUSINESS / ## TECHNICAL)
// so client-side parsing stays uniform.

const BUSINESS_FRAMINGS = {
  // Routing constructs — the *story* is "the workflow makes a decision".
  condition: `1. What decision this step makes, in plain language (1 sentence — say what's being chosen, not how).
2. Which paths exist and when each one is taken, in business terms (e.g., "if the claim is valid we proceed to assessment; otherwise we reject it and email the customer").
3. What happens if no branch matches the condition — does the workflow fall back, error, or silently continue?
4. Why this branching is here — the policy / business rule it enforces, and which downstream teams or systems each branch touches.`,

  // Parallel work — emphasise the why-parallel and what-must-finish.
  fork: `1. Which actions run at the same time and what each one is responsible for, in plain language.
2. Why parallelism makes sense here — what's gained (speed, independence, resilience) versus running them in sequence.
3. What it means for the workflow if one branch is slow or fails — do we wait for everyone, take the fastest, fall back?
4. Where the parallel results converge and which downstream step depends on the combined outcome.`,

  parallel_fork: `1. Which actions run at the same time and what each one is responsible for, in plain language.
2. Why parallelism makes sense here — what's gained (speed, independence, resilience) versus running them in sequence.
3. What it means for the workflow if one branch is slow or fails — do we wait for everyone, take the fastest, fall back?
4. Where the parallel results converge and which downstream step depends on the combined outcome.`,

  // Iteration — the *story* is "we repeat work over a list".
  loop: `1. What thing is being repeated and once-per-what (per claim line item, per detected user, per page of API results, etc.).
2. When the loop stops — the exit condition in plain English (e.g., "until we've processed every claim item", "until the API returns no more pages").
3. Edge cases the audience should know about — what happens with an empty list, a single item, or a body step that fails mid-loop.
4. What downstream consumers expect — a single aggregate output, or N independent outcomes?`,

  do_while: `1. What thing is being repeated and once-per-what (per claim line item, per detected user, per page of API results, etc.).
2. When the loop stops — the exit condition in plain English (e.g., "until we've processed every claim item", "until the API returns no more pages").
3. Edge cases the audience should know about — what happens with an empty list, a single item, or a body step that fails mid-loop.
4. What downstream consumers expect — a single aggregate output, or N independent outcomes?`,

  // Encapsulation — the *story* is "we're calling out to a reusable mini-workflow".
  sub_workflow: `1. What the called sub-workflow is responsible for at a high level (no implementation detail — just the outcome it produces).
2. Why this work is encapsulated as its own workflow rather than inlined here — reusability across other places, ownership boundary, complexity hiding.
3. Whether it runs synchronously (this workflow waits for the result) or fires async (we kick it off and continue) — and what that means for users / downstream tasks.
4. Who owns and maintains the sub-workflow — typically a different team or domain.`,

  async_sub_workflow: `1. What the called sub-workflow is responsible for at a high level (no implementation detail — just the outcome it produces).
2. Why we fire-and-forget rather than wait — the async pattern (e.g., long-running, results delivered via webhook later, or independent of the parent).
3. How the parent eventually learns about completion — typically a Wait-for-Signal step downstream that blocks on a webhook.
4. Failure semantics — what happens to the parent if the async sub-workflow fails (it usually doesn't, by design — but flag the risk).`,

  // Human-in-the-loop.
  approval_gate: `1. What's being approved here, in plain language — who's deciding what.
2. Which user, group, or role is expected to approve, and what UI they see (in-app, email, Slack, etc.).
3. What happens if approval is rejected, expires (timeout), or the workflow needs to recover from a delayed reviewer.
4. Why this human checkpoint exists — the policy / governance / risk requirement it satisfies.`,

  // Suspended waiting.
  signal_wait: `1. What external event the workflow is pausing for (a webhook, a timer, a downstream system signaling completion).
2. How long the workflow is willing to wait, and what happens if the signal never arrives (timeout / failure path).
3. Why we wait here rather than poll — usually because the external system promises an event but doesn't expose a way to check.
4. What happens after the signal arrives — what data it carries and which downstream step consumes it.`,

  // Termination.
  terminate: `1. What outcome this step records when reached (success, failure, business-specific reason).
2. Why the workflow stops here rather than continuing — the upstream condition that justifies an early end.
3. What downstream systems or audit trails see — does this trigger notifications, compensation, or just a clean shutdown?
4. Who consumes the termination signal — operators reviewing failed runs, downstream auto-retry, audit/compliance.`,

  // Notifications / side-effects.
  notification_task: `1. What message is sent and to whom, in plain language.
2. Why this notification fires here in the workflow — what business event it's announcing.
3. What happens if the notification fails to deliver — does the workflow retry, fall back to a different channel, or fail loudly?
4. Who's expected to act on the notification — and what they typically do next.`,

  // Default fallback for plain leaf tasks.
  default: `1. What this step does in 1 sentence (no jargon, no code, no system names).
2. Why it exists in this workflow — what role it plays in the overall outcome.
3. What happens if it fails — the user-facing impact and any recovery the workflow does.
4. Who or what depends on this — systems, teams, downstream tasks.`,
};

const TECHNICAL_FRAMINGS = {
  condition: `- Expression / evaluator type and how each case is matched
- Default-case behaviour and where the workflow lands if no port matches
- Sub-graph cost: how many cases, max depth of any case
- Edge cases (null inputs, type coercion, jq evaluation errors)
- Any performance / hot-path considerations from execution history`,

  fork: `- Branch list and per-branch fan-out shape
- Join / wait mode (ALL vs ANY vs N-of-M) and what's downstream of the join
- Failure propagation: which branch outcomes mark the parent failed
- Resource implications (parallel HTTP calls, rate-limit risk)
- Performance characteristics from execution history`,

  parallel_fork: `- Branch list and per-branch fan-out shape
- Join / wait mode (ALL vs ANY vs N-of-M) and what's downstream of the join
- Failure propagation: which branch outcomes mark the parent failed
- Resource implications (parallel HTTP calls, rate-limit risk)
- Performance characteristics from execution history`,

  loop: `- Iteration source: list expression or condition
- Exit condition and max-iterations safety cap
- Body sub-graph topology and side-effect ordering across iterations
- Failure modes: error within an iteration, empty list, single-item edge case
- Aggregate output shape vs. per-iteration outputs`,

  do_while: `- Iteration source: list expression or condition
- Exit condition and max-iterations safety cap
- Body sub-graph topology and side-effect ordering across iterations
- Failure modes: error within an iteration, empty list, single-item edge case
- Aggregate output shape vs. per-iteration outputs`,

  sub_workflow: `- Referenced workflow name + version and how the version is pinned
- Sync vs async invocation mode and the wait-for-signal pattern (if async)
- Input mapping: which fields from this workflow are passed in
- Output mapping: which fields are read back into the parent context
- Re-entrancy / recursion concerns and the depth limit`,

  async_sub_workflow: `- Referenced workflow name + version and how the version is pinned
- The async-then-wait pattern: paired Wait task that blocks on the completion signal
- Webhook contract: what the external trigger calls back with
- Failure modes: timeout on the wait, retry policy, dead-letter
- Re-entrancy / recursion concerns and the depth limit`,

  approval_gate: `- Approver set: users / groups / role expression
- Channel surfaces (in-app, email, Slack)
- Timeout behaviour and escalation policy
- Audit trail: what's recorded and where (compliance log)
- Recovery: revert, retry, alternate-approver flows`,

  signal_wait: `- Signal type and matching key (correlation id, run id, custom token)
- Timeout duration and the post-timeout fallback path
- Side-channel that triggers the signal (webhook, message queue, scheduler)
- Idempotency: what happens if the signal arrives multiple times
- Performance / dwell time from execution history`,

  terminate: `- Termination status (COMPLETED / FAILED / CANCELLED) and reason field
- Whether parent workflows observe this as their own status
- Side-effects: notifications fired, compensations triggered
- Audit trail and downstream consumers
- Edge cases (terminate inside a sub-workflow vs at root)`,

  notification_task: `- Channel and template / payload format
- Recipient resolution (static address, role expression, expression-resolved)
- Retry policy if delivery fails (HTTP status, channel-specific)
- Rate limiting and de-duplication
- Audit trail of sent notifications`,

  default: `- Configuration breakdown (key fields and what they do)
- Inputs / outputs schema or data shape
- Retry / timeout / error-handling semantics
- Edge cases worth knowing
- Any performance characteristics from the execution history`,
};

// Security & Compliance framing — for the third audience tab. Tells
// the AI to put on a security-reviewer hat and highlight data-flow
// risks, governance gates, secret/PII handling, and audit hooks. We
// DON'T branch deeply per node type here — most security concerns
// (data exposure, injection surface, retention, audit) apply to every
// node — so a single rich framing serves all task types reasonably.
const SECURITY_FRAMINGS = {
  default: `- Data flowing IN to this node: classify it (PII / PHI / financial / secrets / public). Where does it come from?
- Data flowing OUT: where does it go? Logged? Persisted? Sent across a trust boundary?
- Authentication / authorisation: which credential is used, and who controls it?
- Compliance gates that apply (HIPAA / SOC 2 / PCI / GDPR / data residency) — are they enforced upstream, here, or only after the fact?
- Audit trail: what's recorded for this node? Is the recording tamper-evident?
- Failure-mode security risk: if this node mis-fires or is replayed, can sensitive data leak or downstream systems be tricked?
- Concrete recommendations to harden this step (1-2 bullets, only if you spot a real gap).`,
};

function getFraming(nodeType, framingMap) {
  if (!nodeType) return framingMap.default;
  const key = String(nodeType).toLowerCase();
  return framingMap[key] || framingMap.default;
}

// We deliberately ask for plain-language Business framing first — that's
// the audience we win with. Technical detail is collapsed by default.
// The model is instructed to emit two heading-prefixed blocks so we can
// split them on the client. If the model deviates, we fall back to
// rendering the whole response as Business.
function buildExplainPrompt(nodeLabel, nodeType, hasExecHistory, workflowContext) {
  const execNote = hasExecHistory
    ? '\n- Past execution data is included; ground specifics (durations, failure modes) in it where possible.'
    : '';
  const business = getFraming(nodeType, BUSINESS_FRAMINGS);
  const technical = getFraming(nodeType, TECHNICAL_FRAMINGS);

  // Optional workflow-level context — when the user has filled in the
  // Workflow tab on the right panel, these fields land here so the AI
  // can ground its explanation in the business / regulatory / audience
  // realities of the surrounding workflow rather than guessing.
  let contextBlock = '';
  if (workflowContext) {
    const lines = [];
    if (workflowContext.description) lines.push(`- Workflow purpose: ${workflowContext.description}`);
    if (workflowContext.businessContext) lines.push(`- Business / domain context: ${workflowContext.businessContext}`);
    if (workflowContext.audience) lines.push(`- Primary audience for this workflow: ${workflowContext.audience}`);
    if (workflowContext.regulatoryEnv) lines.push(`- Regulatory / compliance environment: ${workflowContext.regulatoryEnv}`);
    if (workflowContext.downstreamSystems) lines.push(`- Downstream systems / consumers: ${workflowContext.downstreamSystems}`);
    if (lines.length > 0) {
      contextBlock = `\n\nAdditional workflow context (use this to ground the Business framing — reference the audience, regulatory environment and downstream systems where relevant):\n${lines.join('\n')}`;
    }
  }

  const security = getFraming(nodeType, SECURITY_FRAMINGS);
  // The backend's `aiCanvasDebug` endpoint historically wrapped the
  // user prompt with debug-specific instructions that pushed the LLM
  // into emitting structured JSON (summary / overallHealth /
  // nodeAnalysis). Rather than fight that, we ASK FOR JSON ourselves
  // with exactly the three keys we want — far more deterministic than
  // hoping the LLM respects markdown heading prefixes.
  return `You are explaining ONE node in a workflow to three different audiences. The node is "${nodeLabel}" (type: ${nodeType}).

Respond with ONLY a JSON object with EXACTLY these three keys: "business", "technical", "security".

The "business" value MUST be 4 short paragraphs (200–300 words total) of plain-language prose for a business / domain stakeholder. NO jargon, NO code, NO system names beyond what a buyer would recognise. Cover:
${business}

The "technical" value MUST be markdown bullet points (under 200 words) for an engineer reviewing the implementation. Cover:
${technical}

The "security" value MUST be markdown bullet points (under 200 words) for an InfoSec / GRC reviewer. Cover:
${security}

CRITICAL RULES:
- Output ONLY the JSON object. No prose before or after, no markdown code fences.
- Do NOT include any other keys (no "summary", no "overallHealth", no "nodeAnalysis").
- Do NOT analyse the workflow as a debugger; this is an explanation, not a debug report.
- Each value is a string. Newlines inside strings are fine; just escape them as \\n.${execNote}${contextBlock}`;
}

// ── Response parsing ──────────────────────────────────────────────
// The prompt asks for a JSON object with three keys (business /
// technical / security). We try in order:
//   1. JSON.parse the raw response
//   2. Pull a {...} block out of surrounding noise (the backend
//      sometimes wraps the model output in explanatory prose or
//      markdown fences) and JSON.parse that
//   3. Fall back to the legacy `## BUSINESS` / `## TECHNICAL` /
//      `## SECURITY` markdown header parser
//   4. Last resort — treat the whole blob as Business so the user
//      sees *something* rather than an empty panel
// We also strip the backend's structured debug-keys (`summary`,
// `overallHealth`, `nodeAnalysis`) when they leak through, mapping
// them to Business so the panel still reads as prose.
function parseExplanation(raw) {
  const empty = { business: '', technical: '', security: '' };
  if (!raw || typeof raw !== 'string') return empty;
  const text = raw.trim();

  const tryJson = (s) => {
    try {
      const parsed = JSON.parse(s);
      if (!parsed || typeof parsed !== 'object') return null;
      // Happy path: model respected the prompt
      if (
        typeof parsed.business === 'string' ||
        typeof parsed.technical === 'string' ||
        typeof parsed.security === 'string'
      ) {
        return {
          business:  typeof parsed.business  === 'string' ? parsed.business  : '',
          technical: typeof parsed.technical === 'string' ? parsed.technical : '',
          security:  typeof parsed.security  === 'string' ? parsed.security  : '',
        };
      }
      // Backend `aiCanvasDebug`-shaped fallback: stringify the debug
      // payload into the Business tab so the user isn't left empty-
      // handed.
      if (parsed.summary || parsed.overallHealth || parsed.nodeAnalysis) {
        const bits = [];
        if (typeof parsed.summary === 'string') bits.push(parsed.summary);
        if (typeof parsed.overallHealth === 'string') bits.push(`Overall: ${parsed.overallHealth}`);
        if (Array.isArray(parsed.nodeAnalysis)) {
          for (const n of parsed.nodeAnalysis) {
            if (n && typeof n === 'object' && (n.label || n.id)) {
              const head = n.label || n.id;
              const body = n.analysis || n.summary || n.description || '';
              if (body) bits.push(`${head} — ${body}`);
            }
          }
        }
        return { business: bits.join('\n\n'), technical: '', security: '' };
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // 1) Direct JSON.parse
  const direct = tryJson(text);
  if (direct) return direct;

  // 2) Look for the first top-level {...} block (handles ```json
  //    fences and "Here is the JSON: { ... }" wrappers)
  const m = text.match(/\{[\s\S]*\}/);
  if (m) {
    const fromBlock = tryJson(m[0]);
    if (fromBlock) return fromBlock;
  }

  // 3) Legacy markdown-heading fallback
  const grab = (header, until) => {
    const re = new RegExp(`##\\s*${header}\\s*\\n([\\s\\S]*?)(?=\\n##\\s*(?:${until.join('|')})|$)`, 'i');
    const mm = text.match(re);
    return mm ? mm[1].trim() : '';
  };
  const business  = grab('BUSINESS',  ['TECHNICAL', 'SECURITY']);
  const technical = grab('TECHNICAL', ['BUSINESS',  'SECURITY']);
  const security  = grab('SECURITY',  ['BUSINESS',  'TECHNICAL']);
  if (business || technical || security) return { business, technical, security };

  // 4) Last resort — show whatever we got in Business
  return { business: text, technical: '', security: '' };
}

// Build the focused subgraph for the API call: selected node + up to 5
// upstream and 5 downstream neighbors. Keeps the prompt within a
// reasonable token budget for any workflow size.
function buildSubgraph(allNodes, allConnections, nodeId) {
  if (!Array.isArray(allNodes) || !Array.isArray(allConnections) || !nodeId) {
    return { nodes: [], connections: [] };
  }
  const upstream = new Set();
  const downstream = new Set();
  for (const c of allConnections) {
    const src = c.source != null ? c.source : c.from;
    const tgt = c.target != null ? c.target : c.to;
    if (tgt === nodeId) upstream.add(src);
    if (src === nodeId) downstream.add(tgt);
  }
  const keep = new Set([nodeId, ...upstream, ...downstream]);
  const nodes = allNodes.filter((n) => keep.has(n.id));
  const connections = allConnections.filter((c) => {
    const src = c.source != null ? c.source : c.from;
    const tgt = c.target != null ? c.target : c.to;
    return keep.has(src) && keep.has(tgt);
  });
  return { nodes, connections };
}

// Lightweight markdown rendering — just bullets and paragraphs. We
// deliberately do NOT pull in a full markdown library for one panel:
// the AI output is constrained, the visual is clean, and a 30-line
// renderer beats a 50KB dependency every time.
function renderBlock(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} style={{ margin: '0 0 8px', paddingLeft: 18, lineHeight: 1.55 }}>
        {listBuf.map((li, i) => <li key={i}>{li}</li>)}
      </ul>
    );
    listBuf = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      listBuf.push(line.slice(2));
    } else {
      flushList();
      out.push(
        <p key={`p-${out.length}`} style={{ margin: '0 0 8px', lineHeight: 1.6 }}>
          {line}
        </p>
      );
    }
  }
  flushList();
  return out;
}

// ── Component ──────────────────────────────────────────────────────

export default function AIExplainerPanel({
  node,
  allNodes,
  allConnections,
  executionState,
  installedConnectors,
  // Whether the panel auto-fetches when `node` changes. Default TRUE
  // because the host now controls WHEN node changes (via double-click
  // or the "Explain selected" button) — the panel just fetches whatever
  // node it's handed, with cache short-circuiting repeats. Set to false
  // only if a host wants to gate fetches with their own user gesture.
  autoFetch = true,
  // Optional workflow-level context the user filled in on the Workflow
  // tab; passed through to the AI prompt so the explanation grounds
  // itself in the business/regulatory environment rather than guessing.
  workflowContext = null,
  // Workflow id — used as a stable cache namespace so two workflows
  // with similarly-named nodes don't collide.
  workflowId = null,
  // Optional: the host's selected node (single-click). When provided
  // AND it's different from the explainer's pinned node, we surface a
  // "Explain selected node" button so the user has an explicit single-
  // click + button trigger path that doesn't conflict with ReactFlow's
  // double-click semantics.
  selectedNode = null,
  // Callback when the user clicks "Explain selected node" — the host
  // updates its `explainerTriggerNodeId` and re-renders us with the
  // new `node` prop.
  onTriggerSelected = null,
}) {
  // In-memory cache mirrors the localStorage cache for fast revisits
  // within the same session — written through to localStorage on every
  // successful fetch so the cache survives reload / collapse / route
  // changes (see utils/aiExplainerCache.js).
  const [cache, setCache] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Active audience tab — 'business' (default) | 'technical' | 'security'.
  const [audience, setAudience] = useState('business');
  // Collapsed = header strip only; expanded = full body with internal
  // scroll. Stays expanded by default so the explanation is visible
  // immediately on trigger.
  const [collapsed, setCollapsed] = useState(false);
  // We track in-flight calls per nodeId. When the user switches node
  // mid-fetch, we abort the previous controller AND mark a cancel flag
  // on the call's local closure so any state-write that races the
  // selection change is dropped silently. Two layers of belt-and-braces
  // because the API helper resolves with `{ok:false}` on AbortError
  // rather than throwing — meaning the .then() runs anyway.
  const abortRef = useRef(null);

  const nodeId = node && node.id;

  // Build the cache key once per fetch — same shape as readEntry /
  // writeEntry expect. Stable across renders for the same inputs.
  // Must precede `cacheKeyForNode` and `fetchExplanation` (both reference
  // it) — declaring it after them tripped a TDZ.
  const buildKey = useCallback((targetNode) => makeCacheKey({
    workflowId,
    nodeId: targetNode && targetNode.id,
    nodeType: targetNode && targetNode.type,
    workflowContext,
  }), [workflowId, workflowContext]);

  const cacheKeyForNode = useMemo(
    () => (nodeId ? buildKey(node) : null),
    [nodeId, node, buildKey],
  );
  const cached = cacheKeyForNode ? cache.get(cacheKeyForNode) : null;
  // If the in-memory Map missed but localStorage has it, surface the
  // persisted value instantly (no spinner) — feels native to revisit.
  const persistedFallback = useMemo(() => {
    if (cached || !cacheKeyForNode) return null;
    return readEntry(cacheKeyForNode);
  }, [cached, cacheKeyForNode]);
  const effectiveCached = cached || persistedFallback;

  const fetchExplanation = useCallback(async (force = false) => {
    if (!node || !node.id) return;
    const targetId = node.id;
    const cacheKey = buildKey(node);

    // Cache lookup order:
    //   1. In-memory Map (fastest; same browser tab session)
    //   2. localStorage (survives reloads / route changes / collapses)
    // We hydrate the in-memory Map from localStorage on read so a
    // subsequent revisit hits the Map without touching localStorage.
    if (!force) {
      if (cache.has(cacheKey)) return;
      const persisted = readEntry(cacheKey);
      if (persisted) {
        setCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, persisted);
          return next;
        });
        return;
      }
    } else {
      // Force = user clicked the refresh icon. Drop both layers so the
      // refetch lands fresh.
      evictEntry(cacheKey);
      setCache((prev) => {
        if (!prev.has(cacheKey)) return prev;
        const next = new Map(prev);
        next.delete(cacheKey);
        return next;
      });
    }

    // Abort any previous in-flight call.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const ac = new AbortController();
    abortRef.current = ac;
    let cancelled = false;
    ac.signal.addEventListener('abort', () => { cancelled = true; }, { once: true });

    setLoading(true);
    setError(null);

    try {
      const subgraph = buildSubgraph(allNodes, allConnections, targetId);
      const execResults = {};
      if (executionState && typeof executionState.forEach === 'function') {
        for (const n of subgraph.nodes) {
          const e = executionState.get(n.id);
          if (e) execResults[n.id] = e;
        }
      }
      const prompt = buildExplainPrompt(
        node.label || targetId,
        node.type || 'task',
        Object.keys(execResults).length > 0,
        workflowContext,
      );
      const connectorIds = Object.keys(installedConnectors || {});
      /* RC-101 — Pass targetNodeId so the BE asks Claude for ONE focused
       * node, not whole-subgraph analysis. Without this, Claude returns
       * nodeAnalysis[] with an entry per subgraph node, label-formatted
       * inconsistently — RC-97i2's matcher then has to guess which entry
       * is "the one" and frequently misses. With targetNodeId, BE rewrites
       * the system prompt to "return a single-entry nodeAnalysis for this
       * node only" → matcher's single-element fallback trivially succeeds. */
      /* RC-109c — mode='explain' switches the BE to a system prompt
       * tuned for the {business, technical, security} contract that
       * buildExplainPrompt above asks for. Pre-RC-109c the BE always
       * used the debug system prompt, which mandated the
       * {summary, nodeAnalysis} shape and silently overrode the FE's
       * user-message contract → all three audience tabs rendered the
       * same content (Business-only fallback). The BE preserves
       * legacy debug behavior when mode is absent / "debug", so this
       * change is additive and zero-regression for the workflow-level
       * debugger callsite in CanvasPageV3. */
      const res = await aiCanvasDebug(prompt, subgraph, execResults, connectorIds, ac.signal, targetId, 'explain');
      if (cancelled) return;

      if (!res || !res.ok) {
        const msg = (res && (res.error || (res.data && res.data.error)))
          || 'Network failure — re-try with the refresh icon.';
        setError(msg);
        return;
      }
      // G429 — Inspect the response payload BEFORE falling through to the
      // "render whatever's in summary" path. CanvasAIService.debugWorkflow
      // wraps Anthropic / parse / config failures inside a HTTP-200
      // JsonNode envelope with `error: <reason>` AND a sentinel
      // `summary: "Error: Unable to generate workflow"` plus a synthetic
      // `warnings[]` array. Pre-G429 the frontend ignored `data.error`
      // on the success path and rendered that sentinel verbatim as the
      // explanation body, which presented as a permanent "Error:
      // Unable to generate workflow" banner on every node and looked
      // like a broken canvas. Surface the real reason as an error
      // instead — if it's an Anthropic-key configuration miss the
      // operator can fix it; if it's transient the user can retry.
      const dataObj = res.data || {};
      const dataErr = typeof dataObj.error === 'string' ? dataObj.error.trim() : '';
      if (dataErr) {
        // Friendly mapping for the most common operational case so users
        // don't see a stack trace fragment when the platform just isn't
        // configured for AI assists yet.
        const friendly = /no.*api.*key|ANTHROPIC_API_KEY|CODE_NO_API_KEY/i.test(dataErr)
          ? 'AI explanations are not configured on this environment — ask your admin to set ANTHROPIC_API_KEY to enable the explainer.'
          : dataErr;
        setError(friendly);
        return;
      }
      /* RC-109c — Happy path: when the BE was called with mode='explain'
       * (current AI Explainer behavior post-RC-109c), it returns the
       * audience-tagged shape directly: { business, technical, security }
       * — exactly what the three tabs render. Short-circuit the
       * legacy nodeAnalysis-extraction path so we don't risk dropping
       * Technical / Security into the synthesized markdown fallback.
       *
       * Detection is permissive: presence of ANY of the three string
       * keys at top level is enough — Claude occasionally drops a key
       * if it has nothing useful to say for that audience, and we'd
       * rather render two tabs accurately than fall through and dump
       * everything into Business.
       *
       * If none of the three keys are present, we fall through to the
       * legacy path (nodeAnalysis extraction → parseExplanation), which
       * remains the contract for the workflow-level debugger panel and
       * for any environment where the BE hasn't rolled out yet. */
      if (
        typeof dataObj.business === 'string' ||
        typeof dataObj.technical === 'string' ||
        typeof dataObj.security === 'string'
      ) {
        const parsed = {
          business:  typeof dataObj.business  === 'string' ? dataObj.business  : '',
          technical: typeof dataObj.technical === 'string' ? dataObj.technical : '',
          security:  typeof dataObj.security  === 'string' ? dataObj.security  : '',
        };
        if (cancelled) return;
        writeEntry(cacheKey, parsed);
        setCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, parsed);
          return next;
        });
        return;
      }

      /* RC-97i / RC-97i2 — Per-node extraction with LENIENT matching.
       *
       * The AI Explainer asks for ONE node's explanation, but the BE
       * debugWorkflow endpoint returns the multi-node analysis shape
       * Claude's debug system prompt asks for:
       *   { summary, overallHealth, nodeAnalysis: [{nodeId, ...}, ...],
       *     flowIssues, quickFixes }
       *
       * Pre-RC-97i the FE grabbed `dataObj.summary` (whole-workflow
       * assessment) — often empty for single-node calls → soft error.
       *
       * RC-97i matched strictly on `nodeId === targetId`. But Claude
       * is inconsistent about which identifier it puts in `nodeId`:
       * sometimes the actual canvas id (`n_007_threat_intel`), sometimes
       * the label ("Threat Intel Lookup - CVE"), sometimes the type
       * (`threat_intel_lookup`). Strict match → cache miss → empty raw
       * → "AI explanation unavailable" soft error on perfectly good
       * Claude responses — the exact symptom Sony reported in RC-97i.
       *
       * RC-97i2 fix — cascading lenient match, narrowest first so
       * we never widen further than necessary:
       *   1. nodeId / id === targetId          (canonical canvas id)
       *   2. nodeId / id === node.label        (Claude often uses label)
       *   3. nodeId / id === node.type         (Claude sometimes uses type)
       *   4. Case-insensitive variants of (2) and (3)
       *   5. SINGLE-element nodeAnalysis     (Claude was asked about
       *      one focused subgraph; one entry = one selected node,
       *      no ambiguity to resolve)
       * Only when ALL miss do we fall back to summary / soft error. */
      let raw = '';
      if (Array.isArray(dataObj.nodeAnalysis) && dataObj.nodeAnalysis.length > 0) {
        const lbl = (node && typeof node.label === 'string') ? node.label.trim() : '';
        const typ = (node && typeof node.type  === 'string') ? node.type.trim()  : '';
        const lblLc = lbl.toLowerCase();
        const typLc = typ.toLowerCase();
        let nodeEntry = dataObj.nodeAnalysis.find((n) => {
          if (!n) return false;
          const nidRaw = (typeof n.nodeId === 'string')
            ? n.nodeId
            : (typeof n.id === 'string' ? n.id : '');
          if (!nidRaw) return false;
          if (nidRaw === targetId) return true;
          if (lbl && nidRaw === lbl) return true;
          if (typ && nidRaw === typ) return true;
          const nidLc = nidRaw.toLowerCase();
          if (lblLc && nidLc === lblLc) return true;
          if (typLc && nidLc === typLc) return true;
          return false;
        });
        // Final fallback: if the explainer is for ONE node and Claude
        // returned exactly ONE analysis entry, trust it regardless of
        // nodeId shape. This catches the "Claude put an array index
        // or arbitrary slug in nodeId" cases without widening to the
        // wrong entry in a multi-entry response.
        if (!nodeEntry && dataObj.nodeAnalysis.length === 1) {
          nodeEntry = dataObj.nodeAnalysis[0];
        }
        if (nodeEntry) {
          /* Build a markdown-style explanation from the per-node
           * structured fields. parseExplanation downstream expects
           * a string; we synthesize one that surfaces every field
           * Claude bothered to populate. Empty fields are skipped
           * silently so the rendered card doesn't show empty
           * sections. */
          const parts = [];
          if (nodeEntry.status && nodeEntry.status !== 'ok') {
            parts.push(`**Status:** ${String(nodeEntry.status).toUpperCase()}`);
          }
          if (nodeEntry.inputAnalysis)  parts.push(`**Inputs:** ${nodeEntry.inputAnalysis}`);
          if (nodeEntry.configAnalysis) parts.push(`**Config:** ${nodeEntry.configAnalysis}`);
          if (nodeEntry.outputAnalysis) parts.push(`**Outputs:** ${nodeEntry.outputAnalysis}`);
          if (Array.isArray(nodeEntry.issues) && nodeEntry.issues.length > 0) {
            parts.push('**Issues:**\n' + nodeEntry.issues.map((s) => `- ${s}`).join('\n'));
          }
          if (Array.isArray(nodeEntry.recommendations) && nodeEntry.recommendations.length > 0) {
            parts.push('**Recommendations:**\n' + nodeEntry.recommendations.map((s) => `- ${s}`).join('\n'));
          }
          raw = parts.join('\n\n').trim();
        }
      }
      // Fall back to the legacy field order if no per-node entry produced text.
      if (!raw) raw = (dataObj.analysis || dataObj.summary) || '';
      // Final guard: if the summary IS the sentinel ("Error: Unable to
      // generate workflow") with no analysis text, treat as error rather
      // than rendering the sentinel as an explanation.
      if (!raw || /^error:\s*unable to generate/i.test(raw.trim())) {
        setError('AI explanation unavailable for this node. Try again, or refresh the explainer.');
        return;
      }
      const parsed = parseExplanation(raw);
      if (cancelled) return;
      // Write through to both layers.
      writeEntry(cacheKey, parsed);
      setCache((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, parsed);
        return next;
      });
    } catch (err) {
      if (cancelled) return;
      if (err && (err.name === 'AbortError' || /aborted/i.test(err.message || ''))) return;
      setError(err && err.message ? err.message : String(err));
    } finally {
      if (!cancelled) setLoading(false);
    }
  }, [node, allNodes, allConnections, executionState, installedConnectors, cache, workflowContext, buildKey]);

  // Auto-fetch when the trigger node changes. Critical: the deps
  // include `fetchExplanation` (new identity every render because its
  // own deps change with each `setLoading` / `setCache`) and `cache`
  // (new Map identity on every set), so this effect re-runs on every
  // render. Without a ref-guard, we'd fire fetchExplanation in a loop
  // — each fetch aborts the previous via abortRef and the call never
  // completes, leaving the empty-state stuck.
  //
  // Ref-guard: track the last cacheKey we triggered a fetch for and
  // bail when re-renders re-fire the effect for the same key. The
  // explicit refresh button calls fetchExplanation(true) directly,
  // bypassing the ref, so manual re-fetch still works.
  const lastTriggeredKeyRef = useRef(null);
  useEffect(() => {
    if (!autoFetch || !node || !node.id) return;
    const key = buildKey(node);
    if (lastTriggeredKeyRef.current === key) return;
    lastTriggeredKeyRef.current = key;
    fetchExplanation(false);
  }, [node, autoFetch, buildKey, fetchExplanation]);

  // Abort any in-flight call when the selected node changes — even if
  // we have a cached entry for the new node, we want any older request
  // for the previous node to stop writing state.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* noop */ }
      }
    };
  }, [nodeId]);

  // ── Render ──────────────────────────────────────────────────────
  // IMPORTANT: ALL hooks (useMemo, useCallback, useEffect, useState …)
  // MUST run on every render in the same order. Any early `return null`
  // therefore goes AFTER the last hook call, never before.
  const headerLabel = useMemo(() => {
    if (node && node.id) return node.label || node.id;
    if (selectedNode && selectedNode.id) return `${selectedNode.label || selectedNode.id} (not analysed yet)`;
    return '';
  }, [node, selectedNode]);

  // True iff there's a selected node that's NOT what we currently
  // have analysed — drives the "Explain this instead" affordance.
  const canExplainSelected = !!(
    selectedNode && selectedNode.id && typeof onTriggerSelected === 'function' &&
    (!node || node.id !== selectedNode.id)
  );

  const accent = 'var(--v2-action)';
  const stone200 = 'var(--v2-border-default)';
  const stone400 = 'var(--v2-text-disabled)';
  const stone500 = 'var(--v2-text-muted)';
  const stone700 = 'var(--v2-text-secondary)';
  const stone900 = 'var(--v2-text-primary)';

  // Render panel even when no node is pinned IF a node is selected so
  // we can show the explicit "Explain this node" CTA. Without either,
  // bail. NB: this guard runs AFTER all hooks above to satisfy React's
  // Rules of Hooks (#310 / #300 otherwise).
  if ((!node || !node.id) && (!selectedNode || !selectedNode.id)) return null;

  return (
    <div style={{
      margin: '0 0 12px',
      padding: 0,
      background: 'var(--v2-bg-surface)',
      border: `1px solid ${stone200}`,
      borderRadius: 8,
      overflow: 'hidden',
    }}>
      {/* Header strip — sparkles + title + collapse + refresh.
          Whole strip is clickable to toggle collapse so users can quickly
          tuck the panel away if they want full NodePropertiesPanel space. */}
      <div
        onClick={() => setCollapsed((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px',
          background: `linear-gradient(135deg, ${accent}10, ${accent}03)`,
          borderBottom: collapsed ? 'none' : `1px solid ${stone200}`,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          background: accent + '20',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Sparkles size={11} color={accent} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: stone500,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>AI Explainer{loading ? ' · generating…' : ''}</div>
          <div style={{
            fontSize: 12, fontWeight: 600, color: stone900,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{headerLabel}</div>
        </div>
        {/* "Explain this instead" — shown when a different node is
            selected (single-click) than the one we've analysed. Single-
            click + this button is the click-only path that doesn't
            collide with ReactFlow's double-click semantics. */}
        {canExplainSelected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onTriggerSelected === 'function') onTriggerSelected();
            }}
            disabled={loading}
            title={`Explain "${selectedNode.label || selectedNode.id}" instead`}
            style={{
              padding: '4px 8px', fontSize: 10, fontWeight: 600,
              background: accent, color: 'var(--v2-bg-surface)',
              border: `1px solid ${accent}`, borderRadius: 4,
              cursor: loading ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              maxWidth: 180,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            <Sparkles size={10} />
            Explain selected
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); fetchExplanation(true); }}
          disabled={loading || (!node || !node.id)}
          title="Re-generate explanation"
          style={{
            padding: '4px 6px', fontSize: 10,
            background: 'transparent', color: stone500,
            border: `1px solid ${stone200}`, borderRadius: 4,
            cursor: (loading || !node || !node.id) ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {loading ? (
            <Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <RefreshCw size={10} />
          )}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCollapsed((v) => !v); }}
          title={collapsed ? 'Expand explainer' : 'Collapse explainer'}
          style={{
            padding: '4px 6px', fontSize: 10,
            background: 'transparent', color: stone500,
            border: `1px solid ${stone200}`, borderRadius: 4,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center',
          }}
        >
          {collapsed ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
        </button>
      </div>

      {/* Audience tab strip — Business / Technical / Security. Visible
          whenever any cached content exists for the active node. */}
      {!collapsed && effectiveCached && (
        <div style={{
          display: 'flex', gap: 0,
          borderBottom: `1px solid ${stone200}`,
          background: 'var(--v2-bg-elevated)',
        }}>
          {[
            { key: 'business',  label: 'Business' },
            { key: 'technical', label: 'Technical' },
            { key: 'security',  label: 'Security & Compliance' },
          ].map((t) => {
            const has = effectiveCached && effectiveCached[t.key] && effectiveCached[t.key].length > 0;
            const active = audience === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setAudience(t.key)}
                disabled={!has}
                title={has ? `View ${t.label} explanation` : `${t.label} block missing — re-run explainer`}
                style={{
                  padding: '7px 10px', fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  color: active ? accent : (has ? stone700 : stone400),
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active ? `2px solid ${accent}` : '2px solid transparent',
                  cursor: has ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  marginBottom: -1,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Body — only visible when not collapsed. maxHeight + internal
          overflow keeps the panel compact even on long answers; the
          NodePropertiesPanel below stays scrollable in its own right. */}
      {!collapsed && (
      <div style={{
        padding: '10px 12px', fontSize: 12, color: stone700,
        maxHeight: 420, overflowY: 'auto',
        // Long node IDs / refs in the body should never push the panel
        // wider than its container — break inside any 50+ char token.
        wordBreak: 'break-word', overflowWrap: 'anywhere',
      }}>
        {loading && !cached && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 0', color: stone500, fontSize: 11,
          }}>
            <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
            Generating explanation…
          </div>
        )}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 6,
            padding: '8px 10px', fontSize: 11,
            background: 'var(--v2-danger-soft)', color: 'var(--v2-accent-rose)',
            border: '1px solid var(--v2-accent-rose-soft)', borderRadius: 6,
          }}>
            <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Couldn't generate explanation</div>
              <div style={{ color: 'var(--v2-danger)', lineHeight: 1.45 }}>{error}</div>
              <button
                type="button"
                onClick={() => fetchExplanation(true)}
                disabled={loading}
                style={{
                  marginTop: 6, padding: '3px 8px', fontSize: 10, fontWeight: 600,
                  color: 'var(--v2-danger)', background: 'var(--v2-bg-surface)',
                  border: '1px solid var(--v2-accent-rose-soft)', borderRadius: 4,
                  cursor: loading ? 'default' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <RefreshCw size={10} />
                Try again
              </button>
            </div>
          </div>
        )}
        {effectiveCached && (() => {
          // Pick the block for the active audience tab. Falls back
          // through Business → Technical → Security if a tab the user
          // landed on happens to be empty (rare; happens if the AI
          // skipped a heading).
          const order = audience === 'business'
            ? ['business', 'technical', 'security']
            : audience === 'technical'
              ? ['technical', 'business', 'security']
              : ['security', 'technical', 'business'];
          const block = order.map((k) => effectiveCached[k]).find((v) => v && v.length > 0);
          if (!block) return null;
          return (
            <div style={{ color: stone900 }}>
              {renderBlock(block)}
            </div>
          );
        })()}
        {!loading && !effectiveCached && !error && (
          <div style={{
            fontSize: 11, color: stone500,
            display: 'flex', flexDirection: 'column', gap: 8,
            padding: '6px 0',
          }}>
            <div style={{ lineHeight: 1.5 }}>
              <b>How to trigger an explanation:</b><br/>
              • <b>Double-click</b> any node, OR<br/>
              • Single-click to select, then press the <b>Explain selected</b> button above.
            </div>
            {canExplainSelected && (
              <button
                type="button"
                onClick={() => {
                  if (typeof onTriggerSelected === 'function') onTriggerSelected();
                }}
                style={{
                  alignSelf: 'flex-start',
                  padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  color: 'var(--v2-bg-surface)', background: accent,
                  border: `1px solid ${accent}`, borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Sparkles size={11} />
                Explain {selectedNode.label || selectedNode.id}
              </button>
            )}
          </div>
        )}
      </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
